import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { BOQAPI } from "../services/api";
import html2pdf from "html2pdf.js";

// ===============================
// HELPERS
// ===============================

const formatNumber = (val: any) => {
  if (val === null || val === undefined || val === "") {
    return "";
  }

  const num = Number(String(val).replace(/,/g, "").trim());

  return Number.isFinite(num) ? num.toFixed(2) : "";
};

const getExcelQuantity = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const cleaned = String(value).replace(/,/g, "").trim();
  const parsed = parseFloat(cleaned);

  if (!isNaN(parsed)) {
    return parsed;
  }

  return value;
};

const sanitizeSheetName = (name: string) => {
  return (
    (name || "BOQ")
      .replace(/[\\/*?:[\]]/g, " ")
      .substring(0, 31)
      .trim() || "BOQ"
  );
};

const cleanText = (text: any) => {
  if (text === null || text === undefined) {
    return "";
  }

  return String(text)
    .replace(/&amp;/g, "&")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\s+\n/g, "\n")
    .trim();
};

const approxRowHeight = (
  text: string,
  min = 18,
  max = 72
) => {
  const lines = Math.max(
    1,
    text.split("\n").length,
    Math.ceil(text.length / 65)
  );

  return Math.min(max, Math.max(min, lines * 15));
};

// ===============================
// NUMBER HELPER
// ===============================

const getNumericQuantity = (value: any): number => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return 0;
  }

  const cleaned = String(value)
    .replace(/,/g, "")
    .trim();

  const parsed = Number(cleaned);

  return Number.isFinite(parsed) ? parsed : 0;
};

// ===============================
// NORMALIZE TEXT FOR GROUPING
// ===============================

const normalizeText = (value: any) => {
  return cleanText(value)
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

// ===============================
// SUMMARIZE WORKS
// ===============================

const summarizeWorks = (works: any[] = []) => {
  const summarizedWorks: any[] = [];
  const groupedRows = new Map<string, any>();

  let currentActivity = "";

  works.forEach((work: any) => {
    if (work.isHeader) {
      currentActivity = cleanText(work.activity);
      return;
    }

    const activity = cleanText(
      work.activity || currentActivity
    );

    const description = cleanText(work.description);
    const unit = cleanText(work.unit);

    // Keep rows with no description separately.
    if (!description) {
      summarizedWorks.push({
        ...work,
        activity,
        description,
        unit,
        quantity: getNumericQuantity(work.quantity),
      });

      return;
    }

    const groupKey = [
      normalizeText(activity),
      normalizeText(description),
      normalizeText(unit),
    ].join("|||");

    const quantity = getNumericQuantity(work.quantity);
    const existingRow = groupedRows.get(groupKey);

    if (existingRow) {
      existingRow.quantity =
        getNumericQuantity(existingRow.quantity) + quantity;
    } else {
      const newRow = {
        ...work,
        activity,
        description,
        unit,
        quantity,
        isHeader: false,
      };

      groupedRows.set(groupKey, newRow);
      summarizedWorks.push(newRow);
    }
  });

  // Sort descriptions alphabetically within each activity.
  // Activity order remains unchanged.

  const sortedWorks: any[] = [];
  const activityOrder: string[] = [];

  summarizedWorks.forEach((work) => {
    const activity = work.activity || "";

    if (!activityOrder.includes(activity)) {
      activityOrder.push(activity);
    }
  });

  activityOrder.forEach((activity) => {
    const activityWorks = summarizedWorks
      .filter(
        (work) => (work.activity || "") === activity
      )
      .sort((a, b) =>
        (a.description || "").localeCompare(
          b.description || "",
          undefined,
          {
            sensitivity: "base",
            numeric: true,
          }
        )
      );

    sortedWorks.push(...activityWorks);
  });

  return sortedWorks;
};

// ===============================
// WORK GROUP COMPONENT
// ===============================

const WorkGroupView = ({ group }: any) => {
  const [expanded, setExpanded] = useState(false);

  const works = summarizeWorks(group.works || []);

  return (
    <div style={{ width: "100%" }}>
      {/* WORK GROUP HEADER */}

      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontWeight: "bold",
          background: "#e9ecef",
          padding: "4px 10px",
          paddingLeft: "30px",
          margin: 0,
          borderBottom: "1px solid #ccc",
          boxSizing: "border-box",
          width: "100%",
          cursor:
            works.length > 0 ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        {works.length > 0 ? (
          <span
            className="pdf-hide-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #666",
              borderRadius: "3px",
              background: "#fff",
              fontSize: "14px",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {expanded ? "−" : "+"}
          </span>
        ) : (
          <span style={{ width: "18px" }} />
        )}

        <span>{group.workGroup}</span>
      </div>

      {/* WORK ITEMS */}

      {expanded &&
        works.map((work: any, wi: number) => (
          <div
            key={`${wi}-${work.activity || ""}-${work.description || ""}`}
            style={{
              margin: 0,
              padding: 0,
              width: "100%",
            }}
          >
            {/* RESPONSIVE ITEM ROW */}

            <div className="boq-item-row">
              {/* ACTIVITY NAME AND DESCRIPTION */}

              <div className="boq-item-text">
                <div className="boq-activity">
                  {work.activity}
                </div>

                <div className="boq-description">
                  {work.description}
                </div>
              </div>

              {/* SUMMED QUANTITY */}

              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              >
                {formatNumber(work.quantity)}
              </div>

              {/* UNIT */}

              <div
                style={{
                  textAlign: "center",
                  color: "#333",
                }}
              >
                {work.unit}
              </div>

              {/* UNIT COST */}

              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              />

              {/* VALUE */}

              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              />
            </div>
          </div>
        ))}
    </div>
  );
};

// ===============================
// TREE NODE COMPONENT
// ===============================

const TreeNodeView = ({
  node,
  level = 0,
}: any) => {
  const [expanded, setExpanded] = useState(false);

  const hasContent =
    (node.groups?.length ?? 0) > 0 ||
    (node.children?.length ?? 0) > 0;

  return (
    <div
      style={{
        paddingLeft: level * 10,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* MAIN NODE HEADER */}

      <div
        onClick={() => {
          if (hasContent) {
            setExpanded(!expanded);
          }
        }}
        style={{
          fontWeight: "bold",
          background: "#d6d8db",
          padding: "6px 10px",
          border: "1px solid #ccc",
          cursor: hasContent
            ? "pointer"
            : "default",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          userSelect: "none",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {hasContent ? (
          <span
            className="pdf-hide-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #666",
              borderRadius: "3px",
              background: "#fff",
              fontSize: "14px",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {expanded ? "−" : "+"}
          </span>
        ) : (
          <span style={{ width: "18px" }} />
        )}

        <span
          style={{
            minWidth: 0,
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          {node.name}
        </span>
      </div>

      {/* CHILD CONTENT */}

      {expanded && (
        <>
          {/* WORK GROUPS */}

          {node.groups?.map(
            (group: any, gi: number) => (
              <WorkGroupView
                key={`${group.workGroup || "group"}-${gi}`}
                group={group}
              />
            )
          )}

          {/* CHILD NODES */}

          {node.children?.map(
            (child: any, i: number) => (
              <TreeNodeView
                key={`${child.id || child.name}-${i}`}
                node={child}
                level={level + 1}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

// ===============================
// MAIN PAGE
// ===============================

const BOQPage = () => {
  const { wbs } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(
    location.search
  );

  const projectName =
    location.state?.projectName ||
    queryParams.get("projectName") ||
    "";

  const selectedWbsName =
    queryParams.get("wbsName") ||
    "";

  const reportRef =
    useRef<HTMLDivElement>(null);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ===============================
  // LOAD DATA
  // ===============================

  useEffect(() => {
    if (wbs) {
      setLoading(true);

      BOQAPI.getByWBS(Number(wbs))
        .then((res) => {
          setData(res);
        })
        .catch(console.error)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [wbs]);

  // ===============================
  // EXPORT PDF
  // ===============================

  const exportToPDF = async () => {
    if (!reportRef.current) {
      return;
    }

    reportRef.current.classList.add(
      "pdf-exporting"
    );

    try {
      const opt = {
        margin: 0.5,
        filename: `${
          projectName || "Project"
        }_TakeOff_Report.pdf`,
        image: {
          type: "jpeg" as const,
          quality: 1,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
        },
        jsPDF: {
          unit: "in" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
        pagebreak: {
          mode: [
            "avoid-all",
            "css",
            "legacy",
          ],
        },
      };

      const pdfWorker = html2pdf()
        .set(opt)
        .from(reportRef.current);

      const pdf = await pdfWorker
        .toPdf()
        .get("pdf");

      const totalPages =
        pdf.internal.getNumberOfPages();

      for (
        let pageNumber = 1;
        pageNumber <= totalPages;
        pageNumber++
      ) {
        pdf.setPage(pageNumber);

        const pageWidth =
          pdf.internal.pageSize.getWidth();

        const pageHeight =
          pdf.internal.pageSize.getHeight();

        pdf.setFontSize(9);
        pdf.setTextColor(100);

        pdf.text(
          `Page ${pageNumber} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 0.25,
          {
            align: "center",
          }
        );
      }

      await pdfWorker.save();
    } finally {
      reportRef.current.classList.remove(
        "pdf-exporting"
      );
    }
  };

  // ===============================
  // EXPORT EXCEL
  // ===============================

  const exportToExcel = async () => {
    const ExcelJS = await import("exceljs");

    const fileSaver: any = await import(
      "file-saver"
    );

    const workbook = new ExcelJS.Workbook();

    workbook.creator = "BaevrLite";
    workbook.created = new Date();

    const reportName =
      projectName ||
      data[0]?.name ||
      "Project";

    const sheetName =
      sanitizeSheetName(reportName);

    const sheet =
      workbook.addWorksheet(sheetName);

    sheet.columns = [
      {
        key: "item",
        width: 70,
      },
      {
        key: "qty",
        width: 14,
      },
      {
        key: "unit",
        width: 12,
      },
      {
        key: "unitCost",
        width: 14,
      },
      {
        key: "value",
        width: 14,
      },
    ];

    sheet.views = [
      {
        state: "frozen",
        ySplit: 2,
      },
    ];

    sheet.properties.defaultRowHeight = 18;

    // ===============================
    // TITLE
    // ===============================

    sheet.getCell("A1").value =
      `Take-Off for ${reportName}`;

    sheet.mergeCells("A1:E1");

    const titleRow = sheet.getRow(1);

    titleRow.height = 22;

    titleRow.font = {
      bold: true,
      size: 14,
    };

    titleRow.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    // ===============================
    // HEADER
    // ===============================

    const headerRow = sheet.getRow(2);

    headerRow.values = [
      "Item",
      "Quantity",
      "Unit",
      "Unit Cost",
      "Value",
    ];

    headerRow.height = 20;

    headerRow.eachCell(
      (cell, colNumber) => {
        cell.font = {
          bold: true,
          color: {
            argb: "FFFFFFFF",
          },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FF5E5E5E",
          },
        };

        cell.alignment = {
          vertical: "middle",
          horizontal:
            colNumber === 1
              ? "left"
              : "center",
        };

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
        };
      }
    );

    let rowIndex = 3;

    // ===============================
    // SECTION ROW STYLE
    // ===============================

    const applySectionRowStyle = (
      row: any,
      indent = 0
    ) => {
      row.height = 18;

      row.getCell(1).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
        indent,
      };

      row.getCell(1).font = {
        bold: true,
      };

      row.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FFE2ECF7",
        },
      };

      for (let c = 1; c <= 5; c++) {
        const cell = row.getCell(c);

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
        };

        if (c > 1) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb: "FFE2ECF7",
            },
          };
        }
      }

      row.getCell(2).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(3).alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      row.getCell(4).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(5).alignment = {
        vertical: "middle",
        horizontal: "right",
      };
    };

    // ===============================
    // ITEM ROW STYLE
    // ===============================

    const applyItemRowStyle = (
      row: any,
      text: string
    ) => {
      row.height = approxRowHeight(text);

      for (let c = 1; c <= 5; c++) {
        const cell = row.getCell(c);

        cell.border = {
          bottom: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
        };

        cell.alignment = {
          vertical: "top",
          wrapText: true,
        };
      }

      row.getCell(1).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };

      row.getCell(2).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(3).alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      row.getCell(4).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(5).alignment = {
        vertical: "middle",
        horizontal: "right",
      };
    };

    // ===============================
    // ADD NODE TO EXCEL
    // ===============================

    const addNode = (
      node: any,
      level = 0
    ) => {
      if (!node) {
        return;
      }

      // MAIN NODE ROW

      const mainRow =
        sheet.getRow(rowIndex++);

      mainRow.getCell(1).value =
        cleanText(node.name || "");

      mainRow.getCell(2).value = "";
      mainRow.getCell(3).value = "";
      mainRow.getCell(4).value = "";
      mainRow.getCell(5).value = "";

      applySectionRowStyle(
        mainRow,
        level
      );

      // WORK GROUPS

      (node.groups || []).forEach(
        (group: any) => {
          const groupRow =
            sheet.getRow(rowIndex++);

          groupRow.getCell(1).value =
            cleanText(
              group.workGroup || ""
            );

          groupRow.getCell(2).value = "";
          groupRow.getCell(3).value = "";
          groupRow.getCell(4).value = "";
          groupRow.getCell(5).value = "";

          applySectionRowStyle(
            groupRow,
            level + 1
          );

          const summarizedWorks =
            summarizeWorks(
              group.works || []
            );

          summarizedWorks.forEach(
            (work: any) => {
              const itemRow =
                sheet.getRow(rowIndex++);

              const itemText = [
                cleanText(
                  work.activity || ""
                ),
                cleanText(
                  work.description || ""
                ),
              ]
                .filter(Boolean)
                .join("\n");

              // ACTIVITY HEADER ROW

              if (work.isHeader) {
                itemRow.getCell(1).value =
                  cleanText(
                    work.activity || ""
                  );

                itemRow.getCell(2).value = "";
                itemRow.getCell(3).value = "";
                itemRow.getCell(4).value = "";
                itemRow.getCell(5).value = "";

                applySectionRowStyle(
                  itemRow,
                  level + 2
                );

                return;
              }

              // SUMMARIZED ITEM ROW

              itemRow.getCell(1).value =
                itemText ||
                cleanText(
                  work.description || ""
                );

              itemRow.getCell(2).value =
                getExcelQuantity(
                  work.quantity
                );

              itemRow.getCell(3).value =
                cleanText(
                  work.unit || ""
                );

              itemRow.getCell(4).value = "";
              itemRow.getCell(5).value = "";

              applyItemRowStyle(
                itemRow,
                itemText ||
                  cleanText(
                    work.description || ""
                  )
              );

              itemRow.getCell(2).numFmt =
                "#,##0.00";

              itemRow.getCell(4).numFmt =
                "#,##0.00";

              itemRow.getCell(5).numFmt =
                "#,##0.00";
            }
          );
        }
      );

      // CHILD NODES

      (node.children || []).forEach(
        (child: any) => {
          addNode(child, level + 1);
        }
      );
    };

    // ADD ALL DATA

    data.forEach((node) => {
      addNode(node, 0);
    });

    // SAVE EXCEL

    const buffer =
      await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    fileSaver.saveAs(
      blob,
      `${reportName}_TakeOff_Report.xlsx`
    );
  };

  // ===============================
  // LOADING
  // ===============================

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        Loading...
      </div>
    );
  }

  // ===============================
  // UI
  // ===============================

  return (
    <div
      className="boq-page"
      style={{
        fontFamily: "Arial, sans-serif",
        background: "#fff",
      }}
    >
      <style>
        {`
          * {
            box-sizing: border-box;
          }

          .boq-page {
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 20px;
            overflow-x: hidden;
          }

          .boq-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
            flex-wrap: wrap;
            width: 100%;
          }

          .boq-title {
            display: flex;
            align-items: center;
            gap: 14px;
            font-size: 20px;
            font-weight: 700;
            min-width: 0;
            flex-wrap: wrap;
          }

          .boq-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }

          .boq-table-header,
          .boq-item-row {
            display: grid;
            grid-template-columns:
              minmax(0, 1fr)
              120px
              80px
              100px
              100px;
            width: 100%;
          }

          .boq-table-header {
            background: #343a40;
            color: white;
            padding: 8px;
            font-weight: bold;
          }

          .boq-item-row {
            padding: 6px 10px;
            padding-left: 60px;
            font-size: 13px;
            border-bottom: 1px solid #ddd;
            background: #fff;
            align-items: start;
          }

          .boq-item-text {
            min-width: 0;
            overflow-wrap: break-word;
            word-break: break-word;
            white-space: normal;
            line-height: 1.5;
          }

          .boq-activity {
            font-weight: 400;
            color: #555;
            margin-bottom: 2px;
            white-space: normal;
            overflow-wrap: break-word;
            word-break: break-word;
          }

          .boq-description {
            color: #555;
            padding-left: 12px;
            white-space: normal;
            overflow-wrap: break-word;
            word-break: break-word;
          }

          .pdf-exporting .pdf-hide-icon {
            display: none !important;
          }

          .pdf-only-header {
            display: none;
          }

          .pdf-exporting .pdf-only-header {
            display: block !important;
          }

          @media (max-width: 768px) {
            .boq-page {
              padding: 10px;
            }

            .boq-header {
              align-items: flex-start;
              gap: 12px;
            }

            .boq-title {
              font-size: 17px;
              gap: 6px;
              width: 100%;
            }

            .boq-title span {
              font-size: 14px !important;
            }

            .boq-actions {
              width: 100%;
              gap: 8px;
            }

            .boq-actions button {
              flex: 1;
              min-width: 120px;
              padding: 9px 10px !important;
              font-size: 12px;
            }

            .boq-table-header,
            .boq-item-row {
              grid-template-columns:
                minmax(0, 1fr)
                65px
                45px
                65px
                65px;
            }

            .boq-item-row {
              padding: 6px 5px;
              padding-left: 30px;
              font-size: 11px;
            }

            .boq-table-header {
              padding: 7px 5px;
              font-size: 11px;
            }

            .boq-item-text {
              font-size: 11px;
              line-height: 1.35;
            }

            .boq-description {
              padding-left: 8px;
            }
          }

          @media (max-width: 480px) {
            .boq-page {
              padding: 6px;
            }

            .boq-title {
              font-size: 15px;
            }

            .boq-table-header,
            .boq-item-row {
              grid-template-columns:
                minmax(0, 1fr)
                52px
                38px
                55px
                55px;
            }

            .boq-item-row {
              padding-left: 20px;
              font-size: 10px;
            }

            .boq-table-header {
              font-size: 10px;
            }

            .boq-item-text {
              font-size: 10px;
              line-height: 1.3;
            }

            .boq-description {
              padding-left: 6px;
            }
          }
        `}
      </style>

      {/* PAGE HEADER */}

      <div className="boq-header">
        <div className="boq-title">
          <span>Bill of Quantities</span>

          <span style={{ color: "#999" }}>
            {" "}
          </span>

          <span
            style={{
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            {projectName || "Project"}
          </span>

          <span style={{ color: "#999" }}>
            {" "}
          </span>

          <span
            style={{
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            {selectedWbsName ||
              wbs ||
              "Selected WBS"}
          </span>
        </div>

        <div className="boq-actions">
          {/* EXPORT EXCEL BUTTON */}

          <button
            onClick={exportToExcel}
            style={{
              background: "#55A878",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Export Excel
          </button>

          {/* EXPORT PDF BUTTON */}

          <button
            onClick={exportToPDF}
            style={{
              background: "#E8757D",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* REPORT CONTENT */}

      <div ref={reportRef}>
        {/* PDF-ONLY REPORT HEADER */}

        <div
          className="pdf-only-header"
          style={{
            textAlign: "center",
            marginBottom: "16px",
            paddingBottom: "10px",
            borderBottom: "1px solid #999",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: 700,
              marginBottom: "6px",
            }}
          >
            Bill of Quantities
          </div>

          <div
            style={{
              fontSize: "14px",
              fontWeight: 700,
            }}
          >
            {projectName || "Project"}{" "}
            {selectedWbsName ||
              wbs ||
              "Selected WBS"}
          </div>
        </div>

        {/* TABLE HEADER */}

        <div className="boq-table-header">
          <div>Item</div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Quantity
          </div>

          <div
            style={{
              textAlign: "center",
            }}
          >
            Unit
          </div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Unit Cost
          </div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Value
          </div>
        </div>

        {/* TABLE BODY */}

        <div
          style={{
            border: "1px solid #ccc",
            borderTop: "none",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          {data.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
              }}
            >
              No data found
            </div>
          ) : (
            data.map((node, i) => (
              <TreeNodeView
                key={`${node.id || node.name}-${i}`}
                node={node}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BOQPage;